<template>
  <div class="jar-dashboard-page bg-light min-vh-100 d-flex flex-column">
    <!-- Platform Header -->
    <the-header ref="theHeader" />

    <!-- Main Content Body Area -->
    <div class="container-fluid jar-content-wrapper py-4 flex-grow-1">
      <!-- Paragraph Description (Top of White Body Area) -->
      <div class="row mb-4">
        <div class="col-12">
          <p class="mb-0 text-secondary work-sans" style="font-size: 1rem; line-height: 1.6; color: #475569 !important;">
            The JAR summarizes key health indicators, progress across strategic pillars, and guiding technical discussions that support the alignment of efforts towards sustainable health outcomes for all Nigerians.
          </p>
        </div>
      </div>

      <!-- Filters Row (Inline Flex Layout matching Figma Crop & custom dropdown card style) -->
      <div class="d-flex flex-wrap align-items-end mb-4 gap-4">
        <!-- Dashboard / Pillar Selector -->
        <div class="filter-item">
          <label class="filter-label font-weight-bold small text-uppercase mb-2 d-block" style="color: #334155 !important; letter-spacing: 0.5px;">
            DASHBOARD
          </label>
          <b-dropdown
            variant="light"
            toggle-class="jar-custom-toggle d-flex align-items-center justify-content-between text-left"
            menu-class="jar-custom-menu p-2"
            no-caret
            style="width: 260px;"
          >
            <template #button-content>
              <span class="text-truncate font-weight-bold" style="color: #1e293b; font-size: 0.95rem;">
                {{ selectedPillarName }}
              </span>
              <b-icon icon="chevron-down" class="ml-2 flex-shrink-0" style="color: #64748b; font-size: 0.85rem;" />
            </template>
            <b-dropdown-item
              v-for="p in pillars"
              :key="p.id"
              :class="{ active: p.id === selectedPillarId }"
              class="jar-custom-item my-1 rounded"
              @click="selectPillar(p.id)"
            >
              {{ p.name }}
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <!-- Indicator Selector -->
        <div class="filter-item">
          <label class="filter-label font-weight-bold small text-uppercase mb-2 d-block" style="color: #334155 !important; letter-spacing: 0.5px;">
            INDICATOR (S)
          </label>
          <b-dropdown
            variant="light"
            toggle-class="jar-custom-toggle d-flex align-items-center justify-content-between text-left"
            menu-class="jar-custom-menu p-2"
            no-caret
            style="width: 380px;"
          >
            <template #button-content>
              <span class="text-truncate font-weight-bold" style="color: #1e293b; font-size: 0.95rem;">
                {{ selectedIndicatorObjName }}
              </span>
              <b-icon icon="chevron-down" class="ml-2 flex-shrink-0" style="color: #64748b; font-size: 0.85rem;" />
            </template>
            <b-dropdown-item
              v-for="ind in activeIndicators"
              :key="ind.id"
              :class="{ active: ind.id === selectedIndicatorId }"
              class="jar-custom-item my-1 rounded"
              @click="selectIndicator(ind.id)"
            >
              {{ ind.name }}
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <!-- Year Selector -->
        <div class="filter-item">
          <label class="filter-label font-weight-bold small text-uppercase mb-2 d-block" style="color: #334155 !important; letter-spacing: 0.5px;">
            YEAR
          </label>
          <b-dropdown
            variant="light"
            toggle-class="jar-custom-toggle d-flex align-items-center justify-content-between text-left"
            menu-class="jar-custom-menu p-2"
            no-caret
            style="width: 150px;"
          >
            <template #button-content>
              <span class="font-weight-bold" style="color: #1e293b; font-size: 0.95rem;">
                {{ selectedYear }}
              </span>
              <b-icon icon="chevron-down" class="ml-2 flex-shrink-0" style="color: #64748b; font-size: 0.85rem;" />
            </template>
            <b-dropdown-item
              v-for="y in availableYears"
              :key="y"
              :class="{ active: y === selectedYear }"
              class="jar-custom-item my-1 rounded"
              @click="selectYear(y)"
            >
              {{ y }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <!-- Active Indicator Title (Plain bold dark uppercase matching design) -->
      <div class="row mb-4">
        <div class="col-12">
          <h2 class="font-weight-bold text-uppercase work-sans" style="color: #1e293b; font-size: 1.25rem; letter-spacing: 0.5px; margin: 0;">
            {{ activeIndicatorName }}
          </h2>
        </div>
      </div>

      <!-- Custom 6-Panel Grid for Contraceptive Prevalence Rate -->
      <div class="row" v-if="selectedIndicatorId === 'contraceptive_prevalence_rate'">
        <!-- Card 1: CPR Trend -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="cprTrendData"
            :viewType="'bar'"
            :indicatorName="'Contraceptive prevalence rate'"
            :unit="'%'"
            :commentary="'Following the decrease between 2013 and 2018, contraceptive prevalence rate witnessed a rise from 14 to 20 in 2023. This process points to effectiveness of advocacies and programs encouraging contraceptive use.'"
            :showCommentary="true"
            :disableMap="true"
            panelId="cpr-trend"
          />
        </div>

        <!-- Card 2: CPR by Age -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="cprAgeData"
            :viewType="'bar'"
            :indicatorName="'CPR by Age'"
            :chartTitle="'CPR by Age'"
            :unit="'%'"
            :commentary="'Despite the progress, there is still a wide disparity contraceptive use in urban and rural areas. Targeted programs may be needed to encourage more rural dwellers to take up contraceptive use. Also, women with ages between 35 and 44 were more likely to use contraceptives than other age groups.\nContraceptive prevalence rate increased significantly as we go up the wealth quintiles. It almost doubled between the second and middle wealth quintile. Education also appeared to be a significant factor in the rate of contraceptive use.\nContraceptive prevalence rate increased with increasing number of living children. However, there was a decline when had five children or more'"
            :showCommentary="true"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="cpr-age"
          />
        </div>

        <!-- Card 3: CPR by Number of living children -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="cprChildrenData"
            :viewType="'bar'"
            :indicatorName="'CPR by Number of living children'"
            :chartTitle="'CPR by Number of living children'"
            :unit="'%'"
            :ruralValue="12.8"
            :urbanValue="30.6"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="cpr-children"
          />
        </div>

        <!-- Card 4: CPR by Wealth quintile -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="cprWealthData"
            :viewType="'bar'"
            :indicatorName="'CPR by Wealth quintile'"
            :chartTitle="'CPR by Wealth quintile'"
            :unit="'%'"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="cpr-wealth"
          />
        </div>

        <!-- Card 5: CPR State Map -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="jarData.primary"
            :viewType="'map'"
            :legend="jarData.legend"
            :showLegend="true"
            :indicatorName="'Contraceptive prevalence rate'"
            :unit="'%'"
            :commentary="'Contraceptive prevalence rate is much higher in the southern part of the country than the North. The rate is as low as 3 in Kebbi. Ekiti has the highest contraceptive prevalence rate with 66'"
            :showCommentary="true"
            panelId="cpr-map"
          />
        </div>

        <!-- Card 6: CPR by Level of education -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="cprEducationData"
            :viewType="'bar'"
            :indicatorName="'CPR by Level of education'"
            :chartTitle="'CPR by Level of education'"
            :unit="'%'"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="cpr-education"
          />
        </div>
      </div>

      <!-- Custom 5-Panel Grid for Antenatal Care Coverage -->
      <div class="row" v-else-if="selectedIndicatorId === 'antenatal_care_coverage'">
        <!-- Card 1: ANC Trend -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="ancTrendData"
            :viewType="'bar'"
            :indicatorName="'Antenatal Care Coverage'"
            :unit="'%'"
            :commentary="'There was a decrease in the rate of pregnant women attending at least four antenatal visits between 2018 and 2023. This shows the need for more efforts towards improving antenatal care among women'"
            :showCommentary="true"
            :disableMap="true"
            panelId="anc-trend"
          />
        </div>

        <!-- Card 2: ANC by Age -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="ancAgeData"
            :viewType="'bar'"
            :indicatorName="'ANC (4 visits) by Age'"
            :chartTitle="'ANC (4 visits) by Age'"
            :unit="'%'"
            :commentary="'Only four in ten pregnant women in the rural areas had up to four antenatal care visits in 2023.\nBetter education and economic strength seemed to translate to better coverage of at least four antenatal visits.\nWomen between the ages of 20 and 34 had more ANC (4 visits) rate than any of the other examined age groups'"
            :showCommentary="true"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="anc-age"
          />
        </div>

        <!-- Card 3: ANC by Education -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="ancEducationData"
            :viewType="'bar'"
            :indicatorName="'ANC (4 visits) by Education'"
            :chartTitle="'ANC (4 visits) by Education'"
            :unit="'%'"
            :ruralValue="41.3"
            :urbanValue="70.5"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="anc-education"
          />
        </div>

        <!-- Card 4: ANC by Wealth quintile -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="ancWealthData"
            :viewType="'bar'"
            :indicatorName="'ANC (4 visits) by Wealth quintile'"
            :chartTitle="'ANC (4 visits) by Wealth quintile'"
            :unit="'%'"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="anc-wealth"
          />
        </div>

        <!-- Card 5: ANC State Map -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="jarData.primary"
            :viewType="'map'"
            :legend="jarData.legend"
            :showLegend="true"
            :indicatorName="'Antenatal Care Coverage'"
            :unit="'%'"
            :commentary="'Kebbi, Sokoto ad Zamfara states had the lowest coverage values for ANC (4 visits). Generally, states in the north had lower coverage values when compared to the south. There needs to be an increase in efforts towards improving antenatal attendance in the country.'"
            :showCommentary="true"
            panelId="anc-map"
          />
        </div>
      </div>

      <!-- Custom 5-Panel Grid for Skilled Birth Attendance -->
      <div class="row" v-else-if="selectedIndicatorId === 'skilled_birth_attendance'">
        <!-- Card 1: SBA Trend -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="sbaTrendData"
            :viewType="'bar'"
            :indicatorName="'Skilled Birth Attendance'"
            :unit="'%'"
            :commentary="'There has been an increase in the rate of deliveries by skilled attendants between 2018 and 2023. This signals an improvement, and efficiency of efforts and campaigns. Efforts should continue towards improving skilled birth attendance.'"
            :showCommentary="true"
            :disableMap="true"
            panelId="sba-trend"
          />
        </div>

        <!-- Card 2: SBA by Age -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="sbaAgeData"
            :viewType="'bar'"
            :indicatorName="'SBA by Age'"
            :chartTitle="'SBA by Age'"
            :unit="'%'"
            :commentary="'Only about 31% of deliveries in rural areas are handled by skilled birth attendants, as against 70% in urban areas. This could be due to availability of facilities in the rural areas. There should be efforts to bridge the gap. With 91% of women with \u201cMore than secondary\u201d education having deliveries taken by SBAs, better education can be said to improve the rates of deliveries by SBAs. Higher financial strength also translated to higher SBA rate.'"
            :showCommentary="true"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="sba-age"
          />
        </div>

        <!-- Card 3: SBA by Education -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="sbaEducationData"
            :viewType="'bar'"
            :indicatorName="'SBA by Education'"
            :chartTitle="'SBA by Education'"
            :unit="'%'"
            :ruralValue="30.6"
            :urbanValue="70.2"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="sba-education"
          />
        </div>

        <!-- Card 4: SBA by Wealth quintile -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="sbaWealthData"
            :viewType="'bar'"
            :indicatorName="'SBA by Wealth quintile'"
            :chartTitle="'SBA by Wealth quintile'"
            :unit="'%'"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="sba-wealth"
          />
        </div>

        <!-- Card 5: SBA State Map -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="jarData.primary"
            :viewType="'map'"
            :legend="jarData.legend"
            :showLegend="true"
            :indicatorName="'Skilled Birth Attendance'"
            :unit="'%'"
            :commentary="'The nothern states largely account for the low Skilled birth attendance rate. States like Kebbi 10, Sokoto 12 and Zamfara 13 have very low values. There is an urgent need for improvement.'"
            :showCommentary="true"
            panelId="sba-map"
          />
        </div>
      </div>

      <!-- Custom 5-Panel Grid for Postnatal Care Coverage -->
      <div class="row" v-else-if="selectedIndicatorId === 'postnatal_care_coverage'">
        <!-- Card 1: PNC Trend -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="pncTrendData"
            :viewType="'bar'"
            :indicatorName="'Postnatal Care Coverage'"
            :unit="'%'"
            :commentary="'There was only a slight increase in Postnatal Care Coverage between 2018 and 2023.'"
            :showCommentary="true"
            :disableMap="true"
            panelId="pnc-trend"
          />
        </div>

        <!-- Card 2: PNC by Age -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="pncAgeData"
            :viewType="'bar'"
            :indicatorName="'PNC by Age'"
            :chartTitle="'PNC by Age'"
            :unit="'%'"
            :commentary="'Women between the ages of 20 and 34 years got had the highest postnatal care coverage rate. In the urban areas, 63% of the women got PNC. This is more than double the rate in rural areas.\nEducation appears to be a factor that increased the chances of women getting postnatal care. However, the wealth quintile an individual belonged to didn\'t seem to have any bearing on this indicator.'"
            :showCommentary="true"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="pnc-age"
          />
        </div>

        <!-- Card 3: PNC by Education -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="pncEducationData"
            :viewType="'bar'"
            :indicatorName="'PNC by Education'"
            :chartTitle="'PNC by Education'"
            :unit="'%'"
            :ruralValue="30.3"
            :urbanValue="63.2"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="pnc-education"
          />
        </div>

        <!-- Card 4: PNC by Wealth quintile -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="pncWealthData"
            :viewType="'bar'"
            :indicatorName="'PNC by Wealth quintile'"
            :chartTitle="'PNC by Wealth quintile'"
            :unit="'%'"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="pnc-wealth"
          />
        </div>

        <!-- Card 5: PNC State Map -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="jarData.primary"
            :viewType="'map'"
            :legend="jarData.legend"
            :showLegend="true"
            :indicatorName="'Postnatal Care Coverage'"
            :unit="'%'"
            :commentary="'States in the north have much lower coverage rates for Postnatal care. Kebbi, Sokoto and Zamfara have the lowest values.'"
            :showCommentary="true"
            panelId="pnc-map"
          />
        </div>
      </div>

      <!-- Custom 4-Panel Grid for Penta 3 Coverage -->
      <div class="row" v-else-if="selectedIndicatorId === 'penta_3_coverage'">
        <!-- Card 1: Penta 3 Trend -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="pentaTrendData"
            :viewType="'bar'"
            :indicatorName="'Penta 3 Coverage'"
            :unit="'%'"
            :commentary="'Between 2018 and 2023, there was a three percent improvement in penta 3 coverage rate. Yet, only 53% of children due for the vaccination took it. More efforts need to go into improving immunization coverage.'"
            :showCommentary="true"
            :disableMap="true"
            panelId="penta-trend"
          />
        </div>

        <!-- Card 2: Penta 3 by Wealth quintile -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="pentaWealthData"
            :viewType="'bar'"
            :indicatorName="'Penta 3 Coverage by Wealth quintile'"
            :chartTitle="'Penta 3 Coverage by Wealth quintile'"
            :unit="'%'"
            :commentary="'Less than half of the children due for penta 3 vaccine in rural areas got it in 2023.\nHigher levels of education and financial strength for mothers increase the chances of children having received penta 3.'"
            :showCommentary="true"
            :ruralValue="45.5"
            :urbanValue="65.4"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="penta-wealth"
          />
        </div>

        <!-- Card 3: Penta 3 by Education -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="pentaEducationData"
            :viewType="'bar'"
            :indicatorName="'Penta 3 Coverage by Education'"
            :chartTitle="'Penta 3 Coverage by Education'"
            :unit="'%'"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="penta-education"
          />
        </div>

        <!-- Card 4: Penta 3 State Bar / Map -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="jarData.primary"
            :viewType="'map'"
            :legend="jarData.legend"
            :showLegend="true"
            :indicatorName="'Penta 3 Coverage'"
            :unit="'%'"
            :commentary="'Kebbi, Zamfara and Sokoto states had the lowest rates for penta 3 coverage. Apart from FCT, states in the north generally had lower coverage rates than their southern counterparts'"
            :showCommentary="true"
            panelId="penta-map"
          />
        </div>
      </div>

      <!-- Custom 4-Panel Grid for Fully Immunized Children -->
      <div class="row" v-else-if="selectedIndicatorId === 'full_immunization_rate'">
        <!-- Card 1: Fully Immunized Trend -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="fullyTrendData"
            :viewType="'bar'"
            :indicatorName="'Fully Immunized Children'"
            :unit="'%'"
            :commentary="'While only 39% of children were fully immunized with basic antigens in 2023, it was a significant improvement when compared to the rate in previous years.'"
            :showCommentary="true"
            :disableMap="true"
            panelId="fully-trend"
          />
        </div>

        <!-- Card 2: Fully Immunized by Wealth quintile -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="fullyWealthData"
            :viewType="'bar'"
            :indicatorName="'Fully Immunized Children by Wealth quintile'"
            :chartTitle="'Fully Immunized Children by Wealth quintile'"
            :unit="'%'"
            :commentary="'Children in the urban areas were more likely to be fully immunized. Higher educational levels and financial strength of the mothers also meant that the children were more likely to be fully immunized.'"
            :showCommentary="true"
            :ruralValue="33.8"
            :urbanValue="47.7"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="fully-wealth"
          />
        </div>

        <!-- Card 3: Fully Immunized by Education -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="fullyEducationData"
            :viewType="'bar'"
            :indicatorName="'Fully Immunized Children by Education'"
            :chartTitle="'Fully Immunized Children by Education'"
            :unit="'%'"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="fully-education"
          />
        </div>

        <!-- Card 4: Fully Immunized State Map -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="jarData.primary"
            :viewType="'map'"
            :legend="jarData.legend"
            :showLegend="true"
            :indicatorName="'Fully Immunized Children'"
            :unit="'%'"
            :commentary="'The rate of fully immunized children in the north was less than the rate in the southern states. Kebbi, Sokoto, Zamfara and Kogi states had the lowest rates.'"
            :showCommentary="true"
            panelId="fully-map"
          />
        </div>
      </div>

      <!-- Custom 4-Panel Grid for Zero Dose Burden -->
      <div class="row" v-else-if="selectedIndicatorId === 'zero_dose_children'">
        <!-- Card 1: Zero Dose Trend -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="zeroTrendData"
            :viewType="'bar'"
            :indicatorName="'Zero Dose Burden'"
            :unit="'%'"
            :commentary="'There was a slight increase in the prevalence of zero dose status among children between 2018 and 2023. This points at a need to improve immunization coverage for children'"
            :showCommentary="true"
            :disableMap="true"
            panelId="zero-trend"
          />
        </div>

        <!-- Card 2: Zero Dose by Wealth quintile -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="zeroWealthData"
            :viewType="'bar'"
            :indicatorName="'Zero Dose Burden by Wealth quintile'"
            :chartTitle="'Zero Dose Burden by Wealth quintile'"
            :unit="'%'"
            :commentary="'One out of every five children in urban areas are zero dose (have not had the first dose of penta vaccine). This number is more than double in the rural areas. There is no much difference in the prevalence of zero dose between male and female children.\nHigher levels of education and financial strength drastically reduce the burden of having zero dose children.'"
            :showCommentary="true"
            :ruralValue="46.9"
            :urbanValue="22.3"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="zero-wealth"
          />
        </div>

        <!-- Card 3: Zero Dose by Education -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="zeroEducationData"
            :viewType="'bar'"
            :indicatorName="'Zero Dose Burden by Education'"
            :chartTitle="'Zero Dose Burden by Education'"
            :unit="'%'"
            :femaleValue="36.1"
            :maleValue="38.0"
            :forceColumn="true"
            :disableMap="true"
            :noSort="true"
            panelId="zero-education"
          />
        </div>

        <!-- Card 4: Zero Dose State Map -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="jarData.primary"
            :viewType="'map'"
            :legend="jarData.legend"
            :showLegend="true"
            :indicatorName="'Zero Dose Burden'"
            :unit="'%'"
            :commentary="'States in the north have a lot more children of zero dose status than their southern counterparts. Kebbi, Sokoto and Zamfara states had the highest zero dose burdens in 2023'"
            :showCommentary="true"
            panelId="zero-map"
          />
        </div>
      </div>

      <!-- Custom 2-Panel Grid for PMTCT -->
      <div class="row" v-else-if="selectedIndicatorId === 'hiv_infants_negative'">
        <!-- Card 1: PMTCT Trend -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NACA"
            :data="pmtctTrendData"
            :viewType="'bar'"
            :indicatorName="'PMTCT (Final Status of HIV Exposed Infants)'"
            :unit="'%'"
            :commentary="'The proportion of HIV exposed infants whose final status remained negative was 90% in 2024 remained at 90%.\nMost states had well over 90% for this indicator. The only exceptions were Kogi 14%, Kebbi, 0% and Anambra 0%. There should be investigations into the issues in these states'"
            :showCommentary="true"
            :disableMap="true"
            panelId="pmtct-trend"
          />
        </div>

        <!-- Card 2: PMTCT State Map -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NACA"
            :data="jarData.primary"
            :viewType="'map'"
            :legend="jarData.legend"
            :showLegend="true"
            :indicatorName="'PMTCT (Final Status of HIV Exposed Infants)'"
            :unit="'%'"
            panelId="pmtct-map"
          />
        </div>
      </div>

      <!-- Custom 2-Panel Grid for Malaria Control -->
      <div class="row" v-else-if="selectedIndicatorId === 'act_fever_under_5'">
        <!-- Card 1: Malaria Control Trend -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="malariaTrendData"
            :viewType="'bar'"
            :indicatorName="'Malaria Control (Children Under 5 Who Received ACT)'"
            :unit="'%'"
            :commentary="'The proportion of children with fever who were treated with ACT increased by 5% between 2018 and 2023.\nWhile some states in the north performed below the national value, most states performed above the national value.\nNiger and Gombe state had the lowest values.\nData for some states were not available.'"
            :showCommentary="true"
            :disableMap="true"
            panelId="malaria-trend"
          />
        </div>

        <!-- Card 2: Malaria Control State Map -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            source="NDHS"
            :data="jarData.primary"
            :viewType="'map'"
            :legend="jarData.legend"
            :showLegend="true"
            :indicatorName="'Malaria Control (Children Under 5 Who Received ACT)'"
            :unit="'%'"
            panelId="malaria-map"
          />
        </div>
      </div>

      <!-- Custom 3-Panel Layout for Health Facility Functionality Analysis -->
      <div class="row" v-else-if="selectedIndicatorId === 'phc_functionality'">
        <!-- Card 1: Donut Chart -->
        <div class="col-lg-5 mb-4">
          <JarPanel
            source="PHDA"
            :data="[]"
            :viewType="'bar'"
            :indicatorName="'Health Facility Functionality Analysis'"
            :disableMap="true"
            :hideToggles="true"
            :customOptions="facilityDonutOptions"
            panelId="facility-donut"
          />
        </div>

        <!-- Card 2: Clustered Columns by Zone -->
        <div class="col-lg-7 mb-4">
          <JarPanel
            source="PHDA"
            :data="[]"
            :viewType="'bar'"
            :indicatorName="'Geopolitical Zone Analysis'"
            :disableMap="true"
            :hideToggles="true"
            :customOptions="facilityZoneClusteredOptions"
            :commentary="'About 70% of the facilities analyzed by PHDA were classified as Functional L1. This means they are able to provide basic essential services. 1331 (21%) are L2; able to provide more advanced services. The rest are either partially functional or non-functional.'"
            :showCommentary="true"
            panelId="facility-zone-clustered"
          />
        </div>

        <!-- Card 3: Stacked Columns by State (Full Row width) -->
        <div class="col-12 mb-4">
          <JarPanel
            source="PHDA"
            :data="[]"
            :viewType="'bar'"
            :indicatorName="'Health Facility Functionality Analysis by State'"
            :disableMap="true"
            :hideToggles="true"
            :customOptions="facilityStateStackedOptions"
            panelId="facility-state-stacked"
          />
        </div>
      </div>

      <!-- Custom Layout for HRH & Community Health Workforce Training -->
      <div class="row" v-else-if="selectedIndicatorId === 'health_workers_training'">
        <!-- Left Card: National Donut Chart -->
        <div class="col-lg-5 mb-4">
          <JarPanel
            source="NHMIS"
            :data="[]"
            :viewType="'bar'"
            :indicatorName="'National Training Average'"
            :disableMap="true"
            :hideToggles="true"
            :customOptions="trainingDonutOptions"
            panelId="training-donut"
          />
        </div>

        <!-- Right Card: State Geopolitical Grid -->
        <div class="col-lg-7 mb-4">
          <div class="jar-panel card border-0 shadow-sm rounded-lg overflow-hidden h-100">
            <!-- Panel Header / Toggles -->
            <div class="panel-header d-flex justify-content-end align-items-center bg-white px-3 py-2 border-bottom">

              <!-- Utility Action Buttons -->
              <div class="d-flex align-items-center utility-actions training-grid-actions">
                <button
                  @click="toggleTrainingFullscreen"
                  class="btn btn-outline-secondary btn-square mx-1 p-0 d-flex align-items-center justify-content-center"
                  title="Toggle Fullscreen"
                >
                  <b-icon :icon="isFullscreenTraining ? 'arrows-angle-contract' : 'arrows-angle-expand'" />
                </button>
                <button
                  @click="downloadTrainingData"
                  class="btn btn-outline-secondary btn-square mx-1 p-0 d-flex align-items-center justify-content-center"
                  title="Download Data"
                >
                  <b-icon icon="download" />
                </button>
              </div>
            </div>

            <!-- Panel Content Area -->
            <div class="panel-body bg-white p-3 d-flex flex-column justify-content-between flex-grow-1">
              <!-- Grid container -->
              <div class="training-grid-wrapper flex-grow-1" style="min-height: 480px;">
                <div class="row" style="margin: 0 -5px;">
                  <div v-for="(states, zone) in trainingGeopoliticalData" :key="zone" class="col" style="padding: 0 5px;">
                    <div class="zone-header text-center font-weight-bold py-2 mb-3 rounded" style="background-color: #f1f5f9; color: #1e293b; font-size: 0.8rem; line-height: 1.2;">
                      {{ zone }}
                    </div>
                    <div v-for="state in states" :key="state.state" class="state-row mb-2" style="font-size: 0.75rem;">
                      <div class="d-flex justify-content-between align-items-center mb-1 text-muted">
                        <span>{{ state.state }}</span>
                      </div>
                      <div class="progress rounded-sm" style="height: 18px; background-color: #fecdd3; border: 1px solid #fda4af; position: relative;">
                        <!-- Trained Segment (Green) -->
                        <div class="progress-bar bg-success" role="progressbar" :style="{ width: state.value + '%', backgroundColor: '#006C47 !important' }">
                          <span v-if="state.value > 0" class="position-absolute w-100 text-center font-weight-bold" style="color: #ffffff; left: 0; line-height: 16px; font-size: 0.65rem;">
                            {{ Math.round(state.value * 10) / 10 }}%
                          </span>
                        </div>
                        <!-- Untrained text overlay if trained is 0% -->
                        <span v-if="state.value === 0" class="position-absolute w-100 text-center font-weight-bold" style="color: #9f1239; left: 0; line-height: 16px; font-size: 0.65rem;">
                          100.0%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Custom Legend -->
                <div class="d-flex align-items-center justify-content-center mt-4" style="gap: 1.5rem; font-size: 0.85rem;">
                  <div class="d-flex align-items-center">
                    <span class="d-inline-block rounded-sm mr-2" style="width: 14px; height: 14px; background-color: #006C47;"></span>
                    <span>Trained</span>
                  </div>
                  <div class="d-flex align-items-center">
                    <span class="d-inline-block rounded-sm mr-2" style="width: 14px; height: 14px; background-color: #fecdd3; border: 1px solid #fda4af;"></span>
                    <span>Untrained</span>
                  </div>
                </div>
              </div>
              <div class="training-commentary-box mt-3 p-3 rounded">
                <p class="small mb-0 pre-line-wrap" style="color: #334155; font-size: 0.95rem; line-height: 1.5;">Only about 37% of primary healthcare workers have been trained nationwide.
At state level, only Gombe state has 100% of healthcare workers trained.
Eleven states have none of their primary healthcare staff trained.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Fullscreen Geopolitical Grid Modal -->
        <b-modal id="modal-expand-training-grid" size="xl" hide-footer centered title="HRH & Community Health Workforce Training (NHMIS)">
          <div class="p-3">
            <div class="training-grid-wrapper overflow-auto" style="max-height: 75vh;">
              <div class="row" style="margin: 0 -5px;">
                <div v-for="(states, zone) in trainingGeopoliticalData" :key="zone" class="col" style="padding: 0 5px; min-width: 150px;">
                  <div class="zone-header text-center font-weight-bold py-2 mb-3 rounded" style="background-color: #f1f5f9; color: #1e293b; font-size: 0.9rem; line-height: 1.2;">
                    {{ zone }}
                  </div>
                  <div v-for="state in states" :key="state.state" class="state-row mb-3" style="font-size: 0.85rem;">
                    <div class="d-flex justify-content-between align-items-center mb-1 text-muted">
                      <span>{{ state.state }}</span>
                    </div>
                    <div class="progress rounded-sm" style="height: 24px; background-color: #fecdd3; border: 1px solid #fda4af; position: relative;">
                      <div class="progress-bar bg-success" role="progressbar" :style="{ width: state.value + '%', backgroundColor: '#006C47 !important' }">
                        <span v-if="state.value > 0" class="position-absolute w-100 text-center font-weight-bold" style="color: #ffffff; left: 0; line-height: 22px; font-size: 0.75rem;">
                          {{ Math.round(state.value * 10) / 10 }}%
                        </span>
                      </div>
                      <span v-if="state.value === 0" class="position-absolute w-100 text-center font-weight-bold" style="color: #9f1239; left: 0; line-height: 22px; font-size: 0.75rem;">
                        100.0%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-modal>
      </div>

      <!-- Default 2-Panel Grid for Other Indicators -->
      <div class="row" v-else-if="jarData">
        <!-- Panel 1 (Primary Data Source) -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            :source="datasources.primary.name"
            :data="jarData.primary"
            :viewType="viewTypePrimary"
            :legend="jarData.legend"
            :commentary="jarData.commentary"
            :showCommentary="true"
            :indicatorName="activeIndicatorName"
            :unit="activeIndicatorUnit"
            @view-change="type => viewTypePrimary = type"
            panelId="primary"
          />
        </div>

        <!-- Panel 2 (Secondary Data Source) -->
        <div class="col-lg-6 mb-4">
          <JarPanel
            :source="datasources.secondary.name"
            :data="jarData.secondary"
            :viewType="viewTypeSecondary"
            :legend="jarData.legend"
            :showLegend="true"
            :indicatorName="activeIndicatorName"
            :unit="activeIndicatorUnit"
            @view-change="type => viewTypeSecondary = type"
            panelId="secondary"
          />
        </div>
      </div>
    </div>

    <!-- Main MSDAT Footer Component -->
    <the-footer />
  </div>
</template>

<script>
import theHeader from '@/modules/msdat-dashboard/views/about/layout/theHeader.vue';
import theFooter from '@/modules/msdat-dashboard/views/about/layout/theFooter.vue';
import JarPanel from '../components/JarPanel.vue';
import { JARPillars, JARDatasources, getJARData } from '../assets/jarConfig';
import {
  JARActualData,
  JARDisaggregatedData,
  JARHealthWorkersTraining,
  JARFacilityFunctionality,
} from '../assets/jarActualData';

export default {
  name: 'JarDashboard',
  components: {
    theHeader,
    theFooter,
    JarPanel,
  },
  data() {
    return {
      pillars: JARPillars,
      selectedPillarId: 'maternal_child_health',
      selectedIndicatorId: 'maternal_mortality_rate',
      selectedYear: 2025,
      viewTypePrimary: 'bar',
      viewTypeSecondary: 'map',
      isFullscreenTraining: false,
    };
  },
  computed: {
    selectedPillarName() {
      const p = this.pillars.find((item) => item.id === this.selectedPillarId);
      return p ? p.name : '';
    },
    selectedIndicatorObjName() {
      const ind = this.activeIndicators.find((item) => item.id === this.selectedIndicatorId);
      return ind ? ind.name : '';
    },
    // List of indicators under the currently active pillar
    activeIndicators() {
      const pillar = this.pillars.find((p) => p.id === this.selectedPillarId);
      return pillar ? pillar.indicators : [];
    },
    // Name of active indicator
    activeIndicatorName() {
      const pillar = this.pillars.find((p) => p.id === this.selectedPillarId);
      if (!pillar) return '';
      const ind = pillar.indicators.find((i) => i.id === this.selectedIndicatorId);
      return ind ? ind.name : '';
    },
    // Measurement unit of active indicator
    activeIndicatorUnit() {
      const pillar = this.pillars.find((p) => p.id === this.selectedPillarId);
      if (!pillar) return '';
      const ind = pillar.indicators.find((i) => i.id === this.selectedIndicatorId);
      return ind ? ind.unit : '';
    },
    // Datasources names based on active indicator
    datasources() {
      return JARDatasources[this.selectedIndicatorId] || JARDatasources.default;
    },
    // The visual dataset (states data, commentary, legends)
    jarData() {
      return getJARData(this.selectedIndicatorId, this.selectedYear);
    },
    // Dynamic available years list based on selected indicator
    availableYears() {
      const indId = this.selectedIndicatorId;
      const indActual = JARActualData[indId];
      if (indActual) {
        const years = Object.keys(indActual).map((y) => parseInt(y, 10));
        return years.sort((a, b) => b - a);
      }
      if (indId === 'phc_functionality' || indId === 'health_workers_training' || indId === 'health_insurance_coverage' || indId === 'vulnerable_subsidies') {
        return [2024];
      }
      return [2025, 2024, 2023];
    },
    cprTrendData() {
      const indActual = JARActualData.contraceptive_prevalence_rate;
      if (!indActual) return [];
      const trend = [];
      const targetYears = ['2013', '2018', '2023'];
      targetYears.forEach((y) => {
        const yData = indActual[y];
        if (yData && yData.NDHS && yData.NDHS.national) {
          const match = yData.NDHS.national.find((n) => n.state === y);
          if (match) {
            trend.push({ state: y, value: match.value });
          } else if (yData.NDHS.national.length > 0) {
            trend.push({ state: y, value: yData.NDHS.national[0].value });
          }
        }
      });
      return trend;
    },
    cprDisaggregated() {
      const diag = JARDisaggregatedData.contraceptive_prevalence_rate;
      return diag ? (diag[this.selectedYear] || diag['2023']) : null;
    },
    cprAgeData() {
      return this.cprDisaggregated ? this.cprDisaggregated.Age : [];
    },
    cprChildrenData() {
      const raw = this.cprDisaggregated ? this.cprDisaggregated['Number of living children'] : [];
      return raw.map((item) => ({
        state: item.state === '0.0' ? '0' : item.state,
        value: item.value,
      }));
    },
    cprWealthData() {
      return this.cprDisaggregated ? this.cprDisaggregated['Wealth quintile'] : [];
    },
    cprEducationData() {
      return this.cprDisaggregated ? this.cprDisaggregated.Education : [];
    },
    ancTrendData() {
      const indActual = JARActualData.antenatal_care_coverage;
      if (!indActual) return [];
      const trend = [];
      const targetYears = ['2013', '2018', '2023'];
      targetYears.forEach((y) => {
        const yData = indActual[y];
        if (yData && yData.NDHS && yData.NDHS.national) {
          const match = yData.NDHS.national.find((n) => n.state === y);
          if (match) {
            trend.push({ state: y, value: match.value });
          } else if (yData.NDHS.national.length > 0) {
            trend.push({ state: y, value: yData.NDHS.national[0].value });
          }
        }
      });
      return trend;
    },
    ancDisaggregated() {
      const diag = JARDisaggregatedData.antenatal_care_coverage;
      return diag ? (diag[this.selectedYear] || diag['2023']) : null;
    },
    ancAgeData() {
      if (!this.ancDisaggregated) return [];
      return this.ancDisaggregated['Mother’s age at birth'] || this.ancDisaggregated["Mother's age at birth"] || [];
    },
    ancEducationData() {
      return this.ancDisaggregated ? this.ancDisaggregated["Mother's Education"] : [];
    },
    ancWealthData() {
      return this.ancDisaggregated ? this.ancDisaggregated['Wealth quintile'] : [];
    },
    sbaTrendData() {
      const indActual = JARActualData.skilled_birth_attendance;
      if (!indActual) return [];
      const trend = [];
      const targetYears = ['2013', '2018', '2023'];
      targetYears.forEach((y) => {
        const yData = indActual[y];
        if (yData && yData.NDHS && yData.NDHS.national) {
          const match = yData.NDHS.national.find((n) => n.state === y);
          if (match) {
            trend.push({ state: y, value: match.value });
          } else if (yData.NDHS.national.length > 0) {
            trend.push({ state: y, value: yData.NDHS.national[0].value });
          }
        }
      });
      return trend;
    },
    sbaDisaggregated() {
      const diag = JARDisaggregatedData.skilled_birth_attendance;
      return diag ? (diag[this.selectedYear] || diag['2023']) : null;
    },
    sbaAgeData() {
      if (!this.sbaDisaggregated) return [];
      return this.sbaDisaggregated['Mother’s age at birth'] || this.sbaDisaggregated["Mother's age at birth"] || [];
    },
    sbaEducationData() {
      return this.sbaDisaggregated ? this.sbaDisaggregated["Mother's Education"] : [];
    },
    sbaWealthData() {
      return this.sbaDisaggregated ? this.sbaDisaggregated['Wealth quintile'] : [];
    },
    pncTrendData() {
      const indActual = JARActualData.postnatal_care_coverage;
      if (!indActual) return [];
      const trend = [];
      const targetYears = ['2013', '2018', '2023'];
      targetYears.forEach((y) => {
        const yData = indActual[y];
        if (yData && yData.NDHS && yData.NDHS.national) {
          const match = yData.NDHS.national.find((n) => n.state === y);
          if (match) {
            trend.push({ state: y, value: match.value });
          } else if (yData.NDHS.national.length > 0) {
            trend.push({ state: y, value: yData.NDHS.national[0].value });
          }
        }
      });
      return trend;
    },
    pncDisaggregated() {
      const diag = JARDisaggregatedData.postnatal_care_coverage;
      return diag ? (diag[this.selectedYear] || diag['2023']) : null;
    },
    pncAgeData() {
      if (!this.pncDisaggregated) return [];
      return this.pncDisaggregated['Mother’s age at birth'] || this.pncDisaggregated["Mother's age at birth"] || [];
    },
    pncEducationData() {
      return this.pncDisaggregated ? this.pncDisaggregated["Mother's Education"] : [];
    },
    pncWealthData() {
      const raw = this.pncDisaggregated ? this.pncDisaggregated['Wealth quintile'] : [];
      const order = ['lowest', 'second', 'middle', 'fourth', 'highest'];
      return [...raw].sort((a, b) => order.indexOf(a.state.toLowerCase()) - order.indexOf(b.state.toLowerCase()));
    },
    pentaTrendData() {
      const indActual = JARActualData.penta_3_coverage;
      if (!indActual) return [];
      const trend = [];
      const targetYears = ['2013', '2018', '2023'];
      targetYears.forEach((y) => {
        const yData = indActual[y];
        if (yData && yData.NDHS && yData.NDHS.national) {
          const match = yData.NDHS.national.find((n) => n.state === y);
          if (match) {
            trend.push({ state: y, value: match.value });
          } else if (yData.NDHS.national.length > 0) {
            trend.push({ state: y, value: yData.NDHS.national[0].value });
          }
        }
      });
      return trend;
    },
    pentaDisaggregated() {
      const diag = JARDisaggregatedData.penta_3_coverage;
      return diag ? (diag[this.selectedYear] || diag['2023']) : null;
    },
    pentaWealthData() {
      return this.pentaDisaggregated ? this.pentaDisaggregated['Wealth quintile'] : [];
    },
    pentaEducationData() {
      return this.pentaDisaggregated ? this.pentaDisaggregated["Mother's Education"] : [];
    },

    fullyTrendData() {
      const indActual = JARActualData.full_immunization_rate;
      if (!indActual) return [];
      const trend = [];
      const targetYears = ['2013', '2018', '2023'];
      targetYears.forEach((y) => {
        const yData = indActual[y];
        if (yData && yData.NDHS && yData.NDHS.national) {
          const match = yData.NDHS.national.find((n) => n.state === y);
          if (match) {
            trend.push({ state: y, value: match.value });
          } else if (yData.NDHS.national.length > 0) {
            trend.push({ state: y, value: yData.NDHS.national[0].value });
          }
        }
      });
      return trend;
    },
    fullyDisaggregated() {
      const diag = JARDisaggregatedData.full_immunization_rate;
      return diag ? (diag[this.selectedYear] || diag['2023']) : null;
    },
    fullyWealthData() {
      return this.fullyDisaggregated ? this.fullyDisaggregated['Wealth quintile'] : [];
    },
    fullyEducationData() {
      return this.fullyDisaggregated ? this.fullyDisaggregated["Mother's Education"] : [];
    },

    zeroTrendData() {
      const indActual = JARActualData.zero_dose_children;
      if (!indActual) return [];
      const trend = [];
      const targetYears = ['2013', '2018', '2023'];
      targetYears.forEach((y) => {
        const yData = indActual[y];
        if (yData && yData.NDHS && yData.NDHS.national) {
          const match = yData.NDHS.national.find((n) => n.state === y);
          if (match) {
            trend.push({ state: y, value: match.value });
          } else if (yData.NDHS.national.length > 0) {
            trend.push({ state: y, value: yData.NDHS.national[0].value });
          }
        }
      });
      return trend;
    },
    zeroDisaggregated() {
      const diag = JARDisaggregatedData.zero_dose_children;
      return diag ? (diag[this.selectedYear] || diag['2023']) : null;
    },
    zeroWealthData() {
      return this.zeroDisaggregated ? this.zeroDisaggregated['Wealth quintile'] : [];
    },
    zeroEducationData() {
      return this.zeroDisaggregated ? this.zeroDisaggregated["Mother's Education"] : [];
    },
    pmtctTrendData() {
      const indActual = JARActualData.hiv_infants_negative;
      if (!indActual) return [];
      const trend = [];
      const targetYears = ['2023', '2024'];
      targetYears.forEach((y) => {
        const yData = indActual[y];
        if (yData && yData.NACA && yData.NACA.national) {
          const match = yData.NACA.national.find((n) => n.state === y);
          if (match) {
            trend.push({ state: y, value: match.value });
          } else if (yData.NACA.national.length > 0) {
            trend.push({ state: y, value: yData.NACA.national[0].value });
          }
        }
      });
      return trend;
    },
    malariaTrendData() {
      const indActual = JARActualData.act_fever_under_5;
      if (!indActual) return [];
      const trend = [];
      const targetYears = ['2013', '2018', '2023'];
      targetYears.forEach((y) => {
        const yData = indActual[y];
        if (yData && yData.NDHS && yData.NDHS.national) {
          const match = yData.NDHS.national.find((n) => n.state === y);
          if (match) {
            trend.push({ state: y, value: match.value });
          } else if (yData.NDHS.national.length > 0) {
            trend.push({ state: y, value: yData.NDHS.national[0].value });
          }
        }
      });
      return trend;
    },
    facilityDataAggregates() {
      const raw = JARFacilityFunctionality || [];
      const map = {};
      raw.forEach((item) => {
        const loc = item.location;
        const lvl = item.level;
        const cnt = item.count;
        if (!map[loc]) {
          map[loc] = {
            total: 0,
            nonFunctional: 0,
            partiallyFunctional: 0,
            functionalL1: 0,
            functionalL2: 0,
          };
        }
        if (lvl === 'Total analyzed facilities') map[loc].total = cnt;
        else if (lvl === 'Non-functional') map[loc].nonFunctional = cnt;
        else if (lvl === 'Partially functional') map[loc].partiallyFunctional = cnt;
        else if (lvl === 'Functional L1') map[loc].functionalL1 = cnt;
        else if (lvl === 'Functional L2') map[loc].functionalL2 = cnt;
      });
      return map;
    },
    facilityDonutOptions() {
      const data = [
        { name: 'Functional L2', y: 1331, color: '#5F945B' },
        { name: 'Functional L1', y: 5779, color: '#99C794' },
        { name: 'Partial Functional', y: 389, color: '#E08B3E' },
        { name: 'Non-functional', y: 767, color: '#C15C3D' },
      ];
      return {
        chart: { type: 'pie', height: 480 },
        title: {
          text: '<b>8,266</b><br>Total Facilities<br>Analyzed',
          align: 'center',
          verticalAlign: 'middle',
          y: -20,
          style: { fontSize: '16px', color: '#1e293b' },
        },
        tooltip: { pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)' },
        plotOptions: {
          pie: {
            innerSize: '65%',
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b><br>{point.y}',
              style: { fontSize: '10px', color: '#475569' },
            },
            showInLegend: true,
          },
        },
        legend: { align: 'center', verticalAlign: 'bottom', layout: 'horizontal' },
        series: [{ name: 'Facilities', data }],
      };
    },
    facilityZoneClusteredOptions() {
      const zoneStates = {
        'North Central': ['Benue', 'FCT', 'Kogi', 'Kwara', 'Nasarawa', 'Niger', 'Plateau', 'FCT Abuja'],
        'North East': ['Adamawa', 'Bauchi', 'Borno', 'Gombe', 'Taraba', 'Yobe'],
        'North West': ['Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Sokoto', 'Zamfara'],
        'South East': ['Abia', 'Anambra', 'Ebonyi', 'Enugu', 'Imo'],
        'South South': ['Akwa Ibom', 'Bayelsa', 'Cross River', 'Delta', 'Edo', 'Rivers'],
        'South West': ['Ekiti', 'Lagos', 'Ogun', 'Ondo', 'Osun', 'Oyo'],
      };

      const zones = Object.keys(zoneStates);
      const aggregates = this.facilityDataAggregates;

      const seriesData = {
        total: [],
        l2: [],
        l1: [],
        partial: [],
        nonFunctional: [],
      };

      zones.forEach((zone) => {
        const states = zoneStates[zone];
        let total = 0;
        let l2 = 0;
        let l1 = 0;
        let partial = 0;
        let nonFunctional = 0;
        states.forEach((st) => {
          const sData = aggregates[st];
          if (sData) {
            total += sData.total || 0;
            l2 += sData.functionalL2 || 0;
            l1 += sData.functionalL1 || 0;
            partial += sData.nonFunctional || 0;
            nonFunctional += sData.partiallyFunctional || 0;
          }
        });
        seriesData.total.push(total);
        seriesData.l2.push(l2);
        seriesData.l1.push(l1);
        seriesData.partial.push(partial);
        seriesData.nonFunctional.push(nonFunctional);
      });

      return {
        chart: { type: 'column', height: 480 },
        title: { text: '' },
        xAxis: { categories: zones },
        yAxis: { title: { text: 'Number of Facilities' } },
        tooltip: { shared: true },
        plotOptions: {
          column: {
            dataLabels: { enabled: true, style: { fontSize: '9px' } },
          },
        },
        series: [
          { name: 'Total Analyzed Facilities', data: seriesData.total, color: '#7E9F74' },
          { name: 'L2 Functional', data: seriesData.l2, color: '#5F945B' },
          { name: 'L1 Functional', data: seriesData.l1, color: '#99C794' },
          { name: 'Partial Functional', data: seriesData.partial, color: '#E08B3E' },
          { name: 'Non-Functional', data: seriesData.nonFunctional, color: '#C15C3D' },
        ],
      };
    },
    facilityStateStackedOptions() {
      const aggregates = this.facilityDataAggregates;
      const states = Object.keys(aggregates)
        .filter((k) => k !== 'North Central' && k !== 'North East' && k !== 'North West' && k !== 'South East' && k !== 'South South' && k !== 'South West')
        .sort();

      const seriesData = {
        l2: [],
        l1: [],
        partial: [],
        nonFunctional: [],
      };

      states.forEach((st) => {
        const sData = aggregates[st];
        seriesData.l2.push(sData.functionalL2 || 0);
        seriesData.l1.push(sData.functionalL1 || 0);
        seriesData.partial.push(sData.nonFunctional || 0);
        seriesData.nonFunctional.push(sData.partiallyFunctional || 0);
      });

      return {
        chart: { type: 'column', height: 500 },
        title: { text: 'Health Facility Functionality Analysis by State' },
        xAxis: { categories: states, labels: { rotation: -45 } },
        yAxis: {
          title: { text: 'Number of Facilities' },
          reversedStacks: false,
        },
        plotOptions: {
          column: {
            stacking: 'normal',
            dataLabels: {
              enabled: true,
              formatter() {
                return this.y > 0 ? this.y : '';
              },
              style: { fontSize: '8px', textOutline: 'none' },
            },
          },
        },
        series: [
          { name: 'Non-functional', data: seriesData.nonFunctional, color: '#C15C3D' },
          { name: 'Partial Functional', data: seriesData.partial, color: '#E08B3E' },
          { name: 'Functional L1', data: seriesData.l1, color: '#99C794' },
          { name: 'Functional L2', data: seriesData.l2, color: '#5F945B' },
        ],
      };
    },
    trainingGeopoliticalData() {
      const zoneStates = {
        'North Central': ['Benue', 'FCT', 'Kogi', 'Kwara', 'Nasarawa', 'Niger', 'Plateau'],
        'North East': ['Adamawa', 'Bauchi', 'Borno', 'Gombe', 'Taraba', 'Yobe'],
        'North West': ['Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Sokoto', 'Zamfara'],
        'South East': ['Abia', 'Anambra', 'Ebonyi', 'Enugu', 'Imo'],
        'South South': ['Akwa Ibom', 'Bayelsa', 'Cross River', 'Delta', 'Edo', 'Rivers'],
        'South West': ['Ekiti', 'Lagos', 'Ogun', 'Ondo', 'Osun', 'Oyo'],
      };

      const raw = JARHealthWorkersTraining || [];
      const res = {};
      Object.keys(zoneStates).forEach((zone) => {
        res[zone] = [];
        zoneStates[zone].forEach((stateName) => {
          const match = raw.find((item) => item.state.toLowerCase() === stateName.toLowerCase());
          const val = match ? match.value : 0;
          res[zone].push({ state: stateName, value: val });
        });
      });
      return res;
    },
    trainingDonutOptions() {
      const data = [
        { name: 'Trained', y: 36.7, color: '#006C47' },
        { name: 'Untrained', y: 63.3, color: '#fecdd3' },
      ];
      return {
        chart: { type: 'pie', height: 480 },
        title: {
          text: 'National Average',
          align: 'center',
          verticalAlign: 'middle',
          y: -20,
          style: { fontSize: '16px', color: '#1e293b', fontWeight: 'bold' },
        },
        tooltip: { pointFormat: '<b>{point.percentage:.1f}%</b>' },
        plotOptions: {
          pie: {
            innerSize: '70%',
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b><br>{point.y}%',
              style: { fontSize: '12px', color: '#475569' },
            },
            showInLegend: true,
          },
        },
        legend: { align: 'center', verticalAlign: 'bottom', layout: 'horizontal' },
        series: [{ name: 'Training Status', data }],
      };
    },
  },
  watch: {
    availableYears: {
      immediate: true,
      handler(newYears) {
        if (newYears && newYears.length > 0) {
          if (!newYears.includes(this.selectedYear)) {
            this.selectedYear = newYears[0];
          }
        }
      },
    },
  },
  methods: {
    // Reset selected indicator to the first item when active strategic pillar changes
    handlePillarChange() {
      if (this.activeIndicators.length > 0) {
        this.selectedIndicatorId = this.activeIndicators[0].id;
      }
    },
    selectPillar(pillarId) {
      this.selectedPillarId = pillarId;
      this.handlePillarChange();
    },
    selectIndicator(indicatorId) {
      this.selectedIndicatorId = indicatorId;
    },
    selectYear(year) {
      this.selectedYear = year;
    },
    toggleTrainingFullscreen() {
      this.isFullscreenTraining = !this.isFullscreenTraining;
      if (this.isFullscreenTraining) {
        this.$bvModal.show('modal-expand-training-grid');
      } else {
        this.$bvModal.hide('modal-expand-training-grid');
      }
    },
    downloadTrainingData() {
      const headers = ['Geopolitical Zone', 'State', 'Trained (%)', 'Untrained (%)'];
      const rows = [];
      const data = this.trainingGeopoliticalData;
      Object.keys(data).forEach((zone) => {
        data[zone].forEach((item) => {
          rows.push([zone, item.state, item.value, 100 - item.value]);
        });
      });
      const csvContent = `data:text/csv;charset=utf-8,${[headers.join(','), ...rows.map((r) => r.join(','))].join('\n')}`;
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'HRH_Community_Health_Workforce_Training.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

.jar-dashboard-page * {
  font-family: 'Inter', sans-serif !important;
}

.jar-banner {
  background-color: #006C47; /* Federal Green Theme */
  border-bottom: 2px solid #005638;
}

.eyebrow {
  letter-spacing: 1px;
}

.bg-success-light {
  background-color: rgba(255, 255, 255, 0.2);
}

.text-white-85 {
  color: rgba(255, 255, 255, 0.85);
}

.max-w-800 {
  max-width: 800px;
}

.border-width-4 {
  border-width: 4px !important;
}

.gap-4 {
  gap: 1.5rem;
}

/* Premium Filter Dropdowns */
.premium-select {
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  height: 42px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
  background-color: #f8fafc;
  padding: 0.375rem 2.25rem 0.375rem 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23475569' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 14px 10px;
}

.premium-select:focus {
  border-color: #006C47;
  box-shadow: 0 0 0 3px rgba(0, 108, 71, 0.15);
  background-color: #ffffff;
  outline: none;
}

.filter-label {
  letter-spacing: 0.5px;
}

.tracking-wide {
  letter-spacing: 0.5px;
}

.jar-content-wrapper {
  padding-left: 3.5rem;
  padding-right: 3.5rem;
}

@media (max-width: 992px) {
  .jar-content-wrapper {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}

@media (max-width: 576px) {
  .jar-content-wrapper {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }
}
/* Training grid card — commentary box matches JarPanel.vue .commentary-box */
.training-commentary-box {
  background-color: #EBF4F6;
  border: 1px solid #CDE5DF;
  color: #1e293b;
  border-radius: 6px;
}

/* Training grid card — action buttons match JarPanel.vue .btn-square */
.training-grid-actions .btn-square {
  width: 32px;
  height: 32px;
  border-color: #cbd5e1;
  border-radius: 6px;
  color: #64748b;
  transition: all 0.2s ease;
}

.training-grid-actions .btn-square:hover {
  background-color: #f1f5f9;
  border-color: #006C47;
  color: #006C47;
}

/* Custom Dropdown Toggle Button */
::v-deep .jar-custom-toggle {
  background-color: #ffffff !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  padding: 0.55rem 1rem !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.2s ease !important;
  width: 100% !important;
}

::v-deep .jar-custom-toggle:hover,
::v-deep .jar-custom-toggle:focus {
  border-color: #006C47 !important;
  box-shadow: 0 0 0 3px rgba(0, 108, 71, 0.1) !important;
}

/* Custom Dropdown Menu Card matching the shared design image */
::v-deep .jar-custom-menu {
  background-color: #e4f1ee !important; /* Soft mint background from mockup image */
  border: 1px solid #c8e2dc !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08) !important;
  min-width: 100% !important;
  margin-top: 6px !important;
  padding: 6px !important;
}

/* Individual Dropdown Item */
::v-deep .jar-custom-item .dropdown-item {
  color: #132e27 !important;
  font-weight: 500 !important;
  font-size: 0.95rem !important;
  padding: 10px 16px !important;
  border-radius: 8px !important;
  background-color: transparent !important;
  white-space: normal !important;
  transition: all 0.15s ease !important;
}

/* Hover state on item */
::v-deep .jar-custom-item .dropdown-item:hover,
::v-deep .jar-custom-item .dropdown-item:focus {
  background-color: #d2e7e2 !important;
  color: #0f2922 !important;
}

/* Active item block matching the design image (rounded pill highlight) */
::v-deep .jar-custom-item.active .dropdown-item,
::v-deep .jar-custom-item .dropdown-item.active {
  background-color: #b8dad3 !important; /* Soft teal pill background */
  color: #0f2922 !important;
  font-weight: 600 !important;
}
</style>
